// src/components/CreateSpace/Step2/hooks/useImageCompression.js
import { useRef, useCallback } from "react";
import {
  TARGET_IMAGE_SIZE_MB,
  MAX_TOTAL_PAYLOAD_MB,
  ALLOWED_IMAGE_TYPES,
} from "../step2.constants";

// Compression presets
const COMPRESSION_PRESETS = {
  STANDARD: {
    JPEG_QUALITIES: [0.8, 0.6, 0.4, 0.3, 0.2],
    PNG_QUALITIES: [0.8, 0.6],
    MAX_DIMENSION: 1920,
    SECOND_PASS_MAX_DIMENSION: 1280,
    MIN_REDUCTION_RATIO: 0.9,
    SMALL_PNG_THRESHOLD_MB: 0.5
  },
  AGGRESSIVE: {
    JPEG_QUALITIES: [0.5, 0.3, 0.2, 0.1],
    PNG_QUALITIES: [0.6, 0.4],
    MAX_DIMENSION: 1280,
    SECOND_PASS_MAX_DIMENSION: 1024,
    MIN_REDUCTION_RATIO: 0.8
  },
  ORIENTATION_FIX: {
    JPEG_QUALITIES: [0.95, 0.92],
    PNG_QUALITIES: [0.95, 0.9],
    MAX_DIMENSION: Infinity,
    SECOND_PASS_MAX_DIMENSION: 2560,
    MIN_REDUCTION_RATIO: 0.98
  }
};

// Orientation constants
const ORIENTATION = {
  TOP_LEFT: 1,
  TOP_RIGHT: 2,
  BOTTOM_RIGHT: 3,
  BOTTOM_LEFT: 4,
  LEFT_TOP: 5,
  RIGHT_TOP: 6,
  RIGHT_BOTTOM: 7,
  LEFT_BOTTOM: 8
};

export const useImageCompression = () => {
  const imageQueueRef = useRef(new Map());
  const originalImagesRef = useRef(new Map());
  const compressionAttemptsRef = useRef(new Map());

  /**
   * Loads image with EXIF data
   */
  const loadImageWithExif = useCallback((src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        if (window.EXIF && typeof window.EXIF.getData === 'function') {
          try {
            window.EXIF.getData(img, function() {
              const orientation = window.EXIF.getTag(this, 'Orientation') || 1;
              console.log(`📊 EXIF orientation: ${orientation}`);
              img.orientation = orientation;
              resolve(img);
            });
          } catch (e) {
            console.error('❌ EXIF processing failed:', e);
            img.orientation = 1;
            resolve(img);
          }
        } else {
          console.warn('⚠️ EXIF not available');
          img.orientation = 1;
          resolve(img);
        }
      };
      
      img.onerror = (err) => {
        console.error('❌ Failed to load image:', err);
        reject(new Error('Failed to load image'));
      };
      
      img.src = src;
    });
  }, []);

  /**
   * Gets orientation from image
   */
  const getOrientation = useCallback((img) => {
    return img.orientation || 1;
  }, []);

  /**
   * Checks if orientation needs fixing
   */
  const needsOrientationFix = useCallback((orientation) => {
    return orientation !== ORIENTATION.TOP_LEFT;
  }, []);

  /**
   * Corrects image orientation
   */
  const correctOrientation = useCallback((img, ctx, width, height) => {
    const orientation = getOrientation(img);
    
    if (orientation === ORIENTATION.TOP_LEFT) {
      return { width, height };
    }
    
    console.log(`🔄 Applying orientation correction: ${orientation}`);
    
    let canvasWidth = width;
    let canvasHeight = height;
    
    if (orientation >= 5 && orientation <= 8) {
      canvasWidth = height;
      canvasHeight = width;
      console.log(`📐 Swapped dimensions to ${canvasWidth}x${canvasHeight}`);
    }
    
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    
    switch (orientation) {
      case ORIENTATION.TOP_RIGHT:
        ctx.translate(canvasWidth, 0);
        ctx.scale(-1, 1);
        break;
      case ORIENTATION.BOTTOM_RIGHT:
        ctx.translate(canvasWidth, canvasHeight);
        ctx.rotate(Math.PI);
        break;
      case ORIENTATION.BOTTOM_LEFT:
        ctx.translate(0, canvasHeight);
        ctx.scale(1, -1);
        break;
      case ORIENTATION.LEFT_TOP:
        ctx.rotate(0.5 * Math.PI);
        ctx.scale(1, -1);
        break;
      case ORIENTATION.RIGHT_TOP:
        ctx.rotate(0.5 * Math.PI);
        ctx.translate(0, -canvasHeight);
        break;
      case ORIENTATION.RIGHT_BOTTOM:
        ctx.rotate(-0.5 * Math.PI);
        ctx.translate(-canvasWidth, 0);
        ctx.scale(1, -1);
        break;
      case ORIENTATION.LEFT_BOTTOM:
        ctx.rotate(-0.5 * Math.PI);
        ctx.translate(-canvasWidth, 0);
        break;
      default:
        break;
    }
    
    return { width: canvasWidth, height: canvasHeight };
  }, [getOrientation]);

  const validateImageType = useCallback((file) => {
    const fileName = file.name || '';
    const extension = fileName.split('.').pop().toLowerCase();
    const validExtensions = ['jpg', 'jpeg', 'png'];
    
    const hasValidMimeType = ALLOWED_IMAGE_TYPES.includes(file.type);
    const hasValidExtension = validExtensions.includes(extension);
    
    if (file.type === 'text/plain' && hasValidExtension) {
      console.log(`📸 File ${file.name} has text/plain type but ${extension} extension - will attempt to use`);
      return true;
    }
    
    return hasValidMimeType || hasValidExtension;
  }, []);

  const calculateDimensions = useCallback((width, height, maxDimension) => {
    if (maxDimension === Infinity || (width <= maxDimension && height <= maxDimension)) {
      return { width, height };
    }
    
    if (width > height) {
      return {
        width: maxDimension,
        height: Math.round((height * maxDimension) / width)
      };
    } else {
      return {
        width: Math.round((width * maxDimension) / height),
        height: maxDimension
      };
    }
  }, []);

  /**
   * Fixes ONLY orientation without unnecessary compression
   */
  const fixOrientationOnly = useCallback(async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = async (event) => {
        try {
          const img = await loadImageWithExif(event.target.result);
          const orientation = getOrientation(img);
          
          if (!needsOrientationFix(orientation)) {
            console.log(`✅ ${file.name}: No orientation fix needed (orientation ${orientation})`);
            resolve(file);
            return;
          }

          console.log(`🔄 ${file.name}: Fixing orientation from ${orientation} to 1`);
          
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          const { width: canvasWidth, height: canvasHeight } = 
            correctOrientation(img, ctx, img.naturalWidth, img.naturalHeight);
          
          canvas.width = canvasWidth;
          canvas.height = canvasHeight;
          
          ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);
          
          const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
          const quality = outputType === 'image/png' ? 0.95 : 0.95;
          
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                console.warn(`⚠️ ${file.name}: Failed to create blob, using original`);
                resolve(file);
                return;
              }
              
              const fixedFile = new File([blob], file.name, {
                type: outputType,
                lastModified: Date.now(),
              });
              
              const originalSizeMB = (file.size / (1024 * 1024)).toFixed(2);
              const newSizeMB = (fixedFile.size / (1024 * 1024)).toFixed(2);
              
              console.log(`✅ ${file.name}: Orientation fixed, size: ${originalSizeMB}MB → ${newSizeMB}MB`);
              resolve(fixedFile);
            },
            outputType,
            quality
          );
        } catch (error) {
          console.error(`❌ ${file.name}: Orientation fix failed:`, error);
          resolve(file);
        }
      };

      reader.onerror = () => {
        console.warn(`⚠️ ${file.name}: FileReader failed, using original`);
        resolve(file);
      };
    });
  }, [loadImageWithExif, getOrientation, needsOrientationFix, correctOrientation]);

  const compressWithDimensions = useCallback(async (
    file, 
    targetSizeMB, 
    preset = COMPRESSION_PRESETS.STANDARD,
    attemptNumber = 1,
    forceJPEG = false
  ) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = async (event) => {
        try {
          console.log('📸 Processing image:', file.name);
          
          const img = await loadImageWithExif(event.target.result);
          
          const isPNG = !forceJPEG && (file.type === 'image/png' || file.name.toLowerCase().endsWith('.png'));
          
          let maxDimension = preset.MAX_DIMENSION;
          if (attemptNumber > 1) {
            maxDimension = preset.SECOND_PASS_MAX_DIMENSION;
          }

          let { width, height } = calculateDimensions(img.naturalWidth, img.naturalHeight, maxDimension);
          
          console.log(`📐 Original: ${img.naturalWidth}x${img.naturalHeight}, Target: ${width}x${height}`);
          
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          const { width: canvasWidth, height: canvasHeight } = correctOrientation(img, ctx, width, height);
          
          canvas.width = canvasWidth;
          canvas.height = canvasHeight;
          
          ctx.drawImage(img, 0, 0, width, height);
          
          console.log(`✅ Canvas created: ${canvas.width}x${canvas.height}`);

          // For PNGs that are too large, convert to JPEG immediately on second pass
          const shouldConvertToJPEG = forceJPEG || (isPNG && attemptNumber > 1 && file.size / (1024 * 1024) > 0.3);
          
          if (shouldConvertToJPEG) {
            console.log(`🔄 Converting PNG to JPEG for better compression`);
            const outputType = 'image/jpeg';
            const quality = attemptNumber > 1 ? 0.6 : 0.8;
            
            canvas.toBlob(
              (blob) => {
                if (blob && blob.size < file.size) {
                  const newFileName = file.name.replace(/\.png$/i, '.jpg');
                  const compressedFile = new File([blob], newFileName, {
                    type: 'image/jpeg',
                    lastModified: Date.now(),
                  });
                  console.log(`✅ PNG converted to JPEG: ${(blob.size / (1024 * 1024)).toFixed(2)}MB`);
                  resolve(compressedFile);
                } else {
                  resolve(file);
                }
              },
              'image/jpeg',
              quality
            );
            return;
          }
          
          if (isPNG) {
            handlePNGCompression(canvas, file, targetSizeMB, preset, attemptNumber, resolve);
          } else {
            handleJPEGCompression(canvas, file, targetSizeMB, preset, attemptNumber, resolve, compressWithDimensions);
          }
        } catch (error) {
          console.error('❌ Image processing failed:', error);
          resolve(file);
        }
      };

      reader.onerror = () => {
        console.warn('⚠️ FileReader failed, using original');
        resolve(file);
      };
    });
  }, [calculateDimensions, correctOrientation, loadImageWithExif]);

  const handlePNGCompression = useCallback((canvas, file, targetSizeMB, preset, attemptNumber, resolve) => {
    const qualities = attemptNumber > 1 
      ? preset.PNG_QUALITIES 
      : COMPRESSION_PRESETS.STANDARD.PNG_QUALITIES;
    
    const tryPNGCompression = (index) => {
      if (index >= qualities.length) {
        // If PNG compression fails completely, convert to JPEG
        console.log(`⚠️ PNG compression failed, converting to JPEG`);
        canvas.toBlob(
          (blob) => {
            if (blob && blob.size < file.size) {
              const newFileName = file.name.replace(/\.png$/i, '.jpg');
              const compressedFile = new File([blob], newFileName, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          0.7
        );
        return;
      }

      canvas.toBlob(
        (blob) => {
          if (!blob) {
            tryPNGCompression(index + 1);
            return;
          }

          const blobSizeMB = blob.size / (1024 * 1024);
          
          if (blobSizeMB <= targetSizeMB || 
              (blobSizeMB < file.size / (1024 * 1024) * 0.7)) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/png',
              lastModified: Date.now(),
            });
            console.log(`✅ PNG compressed: ${blobSizeMB.toFixed(2)}MB`);
            resolve(compressedFile);
          } else {
            tryPNGCompression(index + 1);
          }
        },
        'image/png',
        qualities[index]
      );
    };

    tryPNGCompression(0);
  }, []);

  const handleJPEGCompression = useCallback((canvas, file, targetSizeMB, preset, attemptNumber, resolve, compressWithDimensions) => {
    const qualities = attemptNumber > 1 
      ? preset.JPEG_QUALITIES 
      : COMPRESSION_PRESETS.STANDARD.JPEG_QUALITIES;
    
    let bestBlob = null;
    let bestSize = file.size;
    
    const tryJPEGCompression = (index) => {
      if (index >= qualities.length) {
        if (bestBlob && bestBlob.size < file.size) {
          const bestFile = new File([bestBlob], file.name, {
            type: 'image/jpeg',
            lastModified: Date.now(),
          });
          
          if (bestFile.size / (1024 * 1024) > targetSizeMB && attemptNumber < 3) {
            console.log(`⚠️ Still above target, trying more aggressive compression (attempt ${attemptNumber + 1})`);
            
            compressWithDimensions(
              bestFile, 
              targetSizeMB, 
              COMPRESSION_PRESETS.AGGRESSIVE,
              attemptNumber + 1
            ).then(resolve);
          } else {
            resolve(bestFile);
          }
        } else {
          resolve(file);
        }
        return;
      }

      const quality = qualities[index];
      
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            tryJPEGCompression(index + 1);
            return;
          }

          const blobSizeMB = blob.size / (1024 * 1024);
          
          if (blob.size < bestSize) {
            bestBlob = blob;
            bestSize = blob.size;
          }

          if (blobSizeMB <= targetSizeMB) {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            console.log(`✅ Target reached: ${blobSizeMB.toFixed(2)}MB (quality: ${quality})`);
            resolve(compressedFile);
            return;
          }

          tryJPEGCompression(index + 1);
        },
        'image/jpeg',
        quality
      );
    };

    tryJPEGCompression(0);
  }, []);

  /**
   * Compress single image to target size
   */
  const compressImageToTarget = useCallback(
    async (file, targetSizeMB = TARGET_IMAGE_SIZE_MB, isSecondPass = false) => {
      try {
        if (!validateImageType(file)) {
          console.warn(`⚠️ Invalid image type: ${file.type || 'unknown'}. Using original.`);
          return file;
        }

        console.log(`🔄 ${file.name}: Checking orientation...`);
        const orientedFile = await fixOrientationOnly(file);
        
        const orientedSizeMB = orientedFile.size / (1024 * 1024);
        
        // For second pass, be more aggressive
        if (isSecondPass) {
          console.log(`🔄 ${file.name}: Second pass compression from ${orientedSizeMB.toFixed(2)}MB`);
          const compressedFile = await compressWithDimensions(
            orientedFile, 
            targetSizeMB, 
            COMPRESSION_PRESETS.AGGRESSIVE,
            2,
            true // Force JPEG conversion for PNGs in second pass
          );
          return compressedFile;
        }
        
        if (orientedSizeMB <= targetSizeMB) {
          console.log(`✅ ${file.name}: Orientation fixed, size ${orientedSizeMB.toFixed(2)}MB within target (no compression needed)`);
          return orientedFile;
        }
        
        console.log(`🔄 ${file.name}: ${orientedSizeMB.toFixed(2)}MB → compressing to target ${targetSizeMB}MB`);
        
        const compressedFile = await compressWithDimensions(
          orientedFile, 
          targetSizeMB, 
          COMPRESSION_PRESETS.STANDARD,
          1
        );

        return compressedFile;
      } catch (error) {
        console.error('❌ Unexpected error:', error);
        return file;
      }
    },
    [validateImageType, fixOrientationOnly, compressWithDimensions]
  );

  const compressAllImages = useCallback(
    async (setCompressingImages, setServerErrors, setImageErrors, t) => {
      const warnings = [];
      const imageFiles = [];
      const imageSpecificErrors = {};

      // Collect all images
      for (const [key, file] of originalImagesRef.current.entries()) {
        if (file instanceof File) {
          imageFiles.push({ key, file });
        }
      }

      if (imageFiles.length === 0) {
        return true;
      }

      // Use constants properly
      console.log(`🎯 Target per image from constants: ${TARGET_IMAGE_SIZE_MB}MB`);
      console.log(`📊 Max total payload: ${MAX_TOTAL_PAYLOAD_MB}MB`);
      console.log(`📸 Processing ${imageFiles.length} images`);

      setCompressingImages((prev) => ({
        ...prev,
        all: true,
        message: `Processing ${imageFiles.length} image(s)...`,
      }));

      let totalOriginalSize = 0;
      let totalCompressedSize = 0;
      const compressedResults = [];

      // First pass: fix orientation and compress
      for (const { key, file } of imageFiles) {
        setCompressingImages((prev) => ({ ...prev, [key]: true }));
        totalOriginalSize += file.size;

        try {
          // Use the constant target size
          const resultFile = await compressImageToTarget(file, TARGET_IMAGE_SIZE_MB, false);
          const resultSizeMB = resultFile.size / (1024 * 1024);
          
          // More lenient error checking - only error if > 0.5MB (since target is 0.15MB)
          const MAX_ALLOWED_SIZE_MB = 0.5; // Allow up to 0.5MB before showing error
          
          if (resultSizeMB > MAX_ALLOWED_SIZE_MB) {
            imageSpecificErrors[key] = t?.('validation.imageTooLarge', {
              size: resultSizeMB.toFixed(2),
              target: TARGET_IMAGE_SIZE_MB.toFixed(2)
            }) || `Image is ${resultSizeMB.toFixed(2)}MB, which exceeds the maximum allowed size. Please use a smaller image.`;
          } else {
            // Clear any existing error for this key
            delete imageSpecificErrors[key];
          }
          
          compressedResults.push({ key, file: resultFile, originalFile: file });
        } catch (error) {
          console.error(`❌ Error processing ${key}:`, error);
          compressedResults.push({ key, file, originalFile: file });
          warnings.push(`Using original for ${key} due to processing error`);
          imageSpecificErrors[key] = t?.('validation.compressionError') || 
            `Error compressing image: ${error.message}`;
        } finally {
          setCompressingImages((prev) => ({ ...prev, [key]: false }));
        }
      }

      // Calculate total size after first pass
      let currentTotalSize = compressedResults.reduce(
        (sum, { file }) => sum + file.size, 
        0
      ) / (1024 * 1024);

      console.log(`📊 First pass total: ${currentTotalSize.toFixed(2)}MB / ${MAX_TOTAL_PAYLOAD_MB}MB`);

      // Second pass on ALL images that are still too large
      if (currentTotalSize > MAX_TOTAL_PAYLOAD_MB) {
        console.log(`⚠️ Total payload exceeds limit, performing second pass on all images > 0.3MB`);
        
        // Process ALL images that are larger than 0.3MB
        for (let i = 0; i < compressedResults.length; i++) {
          const { key, file, originalFile } = compressedResults[i];
          const fileSizeMB = file.size / (1024 * 1024);
          
          // Only compress images larger than 0.3MB
          if (fileSizeMB <= 0.3) continue;

          setCompressingImages((prev) => ({ ...prev, [key]: true }));
          
          try {
            console.log(`🔧 Second pass compression for ${key} (${fileSizeMB.toFixed(2)}MB)`);
            
            // More aggressive second pass - target 0.1MB
            const resultFile = await compressImageToTarget(originalFile, 0.1, true);

            const resultSizeMB = resultFile.size / (1024 * 1024);
            
            console.log(`✅ ${key}: Second pass result: ${resultSizeMB.toFixed(2)}MB`);
            
            // Update error status - only error if > 0.5MB
            if (resultSizeMB > 0.5) {
              imageSpecificErrors[key] = t?.('validation.imageTooLarge', {
                size: resultSizeMB.toFixed(2),
                target: TARGET_IMAGE_SIZE_MB.toFixed(2)
              }) || `Image is ${resultSizeMB.toFixed(2)}MB, which exceeds the maximum allowed size.`;
            } else {
              delete imageSpecificErrors[key];
            }

            compressedResults[i].file = resultFile;
          } catch (error) {
            console.error(`❌ Second pass error for ${key}:`, error);
          } finally {
            setCompressingImages((prev) => ({ ...prev, [key]: false }));
          }
        }

        // Recalculate total size
        currentTotalSize = compressedResults.reduce(
          (sum, { file }) => sum + file.size, 
          0
        ) / (1024 * 1024);
        
        console.log(`📊 Second pass total: ${currentTotalSize.toFixed(2)}MB`);
      }

      // Final check - only error if total exceeds limit
      if (currentTotalSize > MAX_TOTAL_PAYLOAD_MB) {
        setServerErrors((prev) => [
          ...prev,
          {
            code: 'total_payload_too_large',
            message: t?.('validation.payloadTooLarge', {
              current: currentTotalSize.toFixed(2),
              max: MAX_TOTAL_PAYLOAD_MB
            }) || `Total images (${currentTotalSize.toFixed(2)}MB) exceed the ${MAX_TOTAL_PAYLOAD_MB}MB limit. Please remove some images.`,
            type: 'error'
          },
        ]);
      } else {
        // Clear total errors if under limit
        setServerErrors((prev) => prev.filter(e => e.code !== 'total_payload_too_large'));
      }

      // Store final results and update error status
      for (const { key, file } of compressedResults) {
        const originalFile = originalImagesRef.current.get(key);
        imageQueueRef.current.set(key, file);
        totalCompressedSize += file.size;
        
        const fileSizeMB = file.size / (1024 * 1024);
        const savings = originalFile ? ((originalFile.size - file.size) / originalFile.size * 100).toFixed(0) : 0;
        
        if (file.size < originalFile.size) {
          console.log(`✅ ${key}: Saved ${savings}% (now ${fileSizeMB.toFixed(2)}MB)`);
        } else {
          console.log(`ℹ️ ${key}: No changes needed, size ${fileSizeMB.toFixed(2)}MB`);
        }
        
        // Final error check - only error if > 0.5MB
        if (fileSizeMB > 0.5) {
          imageSpecificErrors[key] = t?.('validation.imageTooLarge', {
            size: fileSizeMB.toFixed(2),
            target: TARGET_IMAGE_SIZE_MB.toFixed(2)
          }) || `Image is ${fileSizeMB.toFixed(2)}MB, which is too large. Please use a smaller image.`;
        } else {
          // Clear any existing error for this key
          delete imageSpecificErrors[key];
        }
      }

      // Set image-specific errors (only those that remain)
      if (Object.keys(imageSpecificErrors).length > 0) {
        setImageErrors(imageSpecificErrors);
        console.log(`⚠️ Final image errors:`, imageSpecificErrors);
      } else {
        setImageErrors({});
        console.log(`✅ No image errors remaining`);
      }

      setCompressingImages((prev) => ({ ...prev, all: false }));

      const totalOriginalMB = (totalOriginalSize / (1024 * 1024)).toFixed(2);
      const totalCompressedMB = (totalCompressedSize / (1024 * 1024)).toFixed(2);
      console.log(`📊 Final total: ${totalOriginalMB}MB → ${totalCompressedMB}MB`);

      if (warnings.length > 0) {
        setServerErrors((prev) => [
          ...prev,
          { message: warnings.join('. '), type: 'warning' },
        ]);
      }

      return {
        success: true,
        errors: imageSpecificErrors
      };
    },
    [compressImageToTarget]
  );

  const calculateTotalPayloadSize = useCallback(async () => {
    let totalSize = 0;
    for (const file of imageQueueRef.current.values()) {
      if (file instanceof File) {
        totalSize += file.size;
      }
    }
    return totalSize / (1024 * 1024);
  }, []);

  const getImageInfo = useCallback(() => {
    let totalOriginal = 0;
    let totalCompressed = 0;

    for (const [key, originalFile] of originalImagesRef.current.entries()) {
      const compressedFile = imageQueueRef.current.get(key);
      if (originalFile instanceof File) totalOriginal += originalFile.size;
      if (compressedFile instanceof File) totalCompressed += compressedFile.size;
    }

    return {
      count: originalImagesRef.current.size,
      totalOriginal: (totalOriginal / (1024 * 1024)).toFixed(2),
      totalCompressed: (totalCompressed / (1024 * 1024)).toFixed(2),
      savings: totalOriginal > 0 
        ? ((totalOriginal - totalCompressed) / totalOriginal * 100).toFixed(0) 
        : 0,
    };
  }, []);

const clearImage = useCallback((key) => {
  console.log(`🧹 [useImageCompression] Clearing image: ${key}`);
  
  // Log before clearing
  console.log(`   Before clear - imageQueueRef has:`, Array.from(imageQueueRef.current.keys()));
  console.log(`   Before clear - originalImagesRef has:`, Array.from(originalImagesRef.current.keys()));
  
  // Clear from all refs
  const deletedFromQueue = imageQueueRef.current.delete(key);
  const deletedFromOriginal = originalImagesRef.current.delete(key);
  const deletedFromAttempts = compressionAttemptsRef.current.delete(key);
  
  console.log(`   Deleted from queue: ${deletedFromQueue}`);
  console.log(`   Deleted from original: ${deletedFromOriginal}`);
  console.log(`   Deleted from attempts: ${deletedFromAttempts}`);
  
  // Log after clearing
  console.log(`   After clear - imageQueueRef has:`, Array.from(imageQueueRef.current.keys()));
  console.log(`   After clear - originalImagesRef has:`, Array.from(originalImagesRef.current.keys()));
}, []);

  const clearAllImages = useCallback(() => {
    imageQueueRef.current.clear();
    originalImagesRef.current.clear();
    compressionAttemptsRef.current.clear();
  }, []);

  return {
    imageQueueRef,
    originalImagesRef,
    compressImageToTarget,
    compressAllImages,
    calculateTotalPayloadSize,
    getImageInfo,
    validateImageType,
    clearImage,
    clearAllImages,
  };
};